import { useLayoutEffect, useState } from 'react';

import {
  EQueryKeys,
  EValidSSOKey,
  IUseInitSSOInput,
  IUseInitSSOOuput,
  IInitSSOData,
  IInitSSOError,
} from '../../../types';
import { useAuthStore } from '../store';
import { retrieveSessionStorage, checkObject } from '../utils/helpers';

export const useInitSSO = (): IUseInitSSOOuput => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isData, setData] = useState<IInitSSOData>(null);
  const [isError, setError] = useState<IInitSSOError>(null);
  const setCurrentAppState = useAuthStore((s) => s.setCurrentAppState);

  const getFullRedirectUri = (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const params = new URLSearchParams(parsedUrl.search);

      let redirectUri = params.get(EQueryKeys.REDIRECT_URI);

      if (!redirectUri) throw new Error('Failed to retrieve value of redirect_uri');

      const mainParams = [
        EQueryKeys.APP_TYPE,
        EQueryKeys.ROUTE_TYPE,
        EQueryKeys.REDIRECT_URI,
      ];
      const extraParams: string[] = [];

      for (const [key, value] of params.entries()) {
        if (!mainParams.includes(key as EQueryKeys)) {
          extraParams.push(`${key}=${value}`);
        }
      }

      if (extraParams.length > 0) {
        if (redirectUri.includes('?')) {
          redirectUri += '&' + extraParams.join('&');
        } else {
          redirectUri += '?' + extraParams.join('&');
        }
      }

      return redirectUri;
    } catch {
      return null;
    }
  };

  useLayoutEffect(() => {
    const initSSO = () => {
      try {
        setLoading(true);

        const urlData = new URL(location.href);
        const completeRedirectURI = getFullRedirectUri(location.href);

        if (!completeRedirectURI) {
          throw new Error('Failed to retrieve the full redirect-uri value');
        }

        const ssoRedirectData: IUseInitSSOInput = {
          app_type: urlData.searchParams.get(EQueryKeys.APP_TYPE),
          route_type: urlData.searchParams.get(EQueryKeys.ROUTE_TYPE),
          redirect_uri: completeRedirectURI,
        };

        const isValidSSOQueryData = Object.values(ssoRedirectData).every(
          (value) => value !== null
        );

        const requiredSessionKeys = [
          EQueryKeys.APP_TYPE,
          EQueryKeys.ROUTE_TYPE,
          EQueryKeys.REDIRECT_URI,
        ];
        const retrieveSessionDataResult = retrieveSessionStorage(requiredSessionKeys);
        const isValidSSOSessionData = retrieveSessionDataResult
          ? checkObject(retrieveSessionDataResult, requiredSessionKeys)
          : null;

        sessionStorage.setItem(EValidSSOKey.VALID_SSO, 'false');

        if (!isValidSSOQueryData && !isValidSSOSessionData) return;

        sessionStorage.setItem(EValidSSOKey.VALID_SSO, 'true');

        if (isValidSSOQueryData) {
          setCurrentAppState(ssoRedirectData.app_type as string);

          Object.keys(ssoRedirectData).forEach((key) => {
            sessionStorage.setItem(
              key,
              ssoRedirectData[key as keyof IUseInitSSOInput] as string
            );
          });

          setData(ssoRedirectData);
        } else if (isValidSSOSessionData && retrieveSessionDataResult) {
          setCurrentAppState(retrieveSessionDataResult.app_type as string);

          (Object.keys(retrieveSessionDataResult) as Array<keyof typeof retrieveSessionDataResult>).forEach((key) => {
            if (retrieveSessionDataResult[key] !== null) {
              sessionStorage.setItem(key, retrieveSessionDataResult[key] as string);
              return;
            }
            sessionStorage.removeItem(key);
          });

          setData(retrieveSessionDataResult as IUseInitSSOInput);
        }

        setLoading(false);
      } catch (err) {
        sessionStorage.setItem(EValidSSOKey.VALID_SSO, 'false');
        setError(err as Error);
        setLoading(false);
      }
    };

    initSSO();
  }, []);

  return {
    initSSOLoading: isLoading,
    initSSOData: isData,
    initSSOError: isError,
  };
};
